import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CurrentUser, LoginInput, LoginResponse } from '@brimostech/shared';
import { api, hasSessionHint, refreshSession, session } from '@/lib/api-client';
import { AuthContext, type AuthStatus } from './auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = session.subscribe((next) => {
      if (cancelled) return;
      if (next === null) {
        setUser(null);
        setStatus('anonymous');
        queryClient.clear();
      } else {
        setUser(next);
        setStatus('authenticated');
      }
    });
    // Restore the session from the httpOnly refresh cookie on first load — only when a session
    // was left behind in this browser (a fresh one would just collect a 401).
    const restore = hasSessionHint() ? refreshSession() : Promise.resolve(null);
    void restore.then((result) => {
      if (cancelled) return;
      if (result?.user) {
        setUser(result.user);
        setStatus('authenticated');
      } else {
        setUser(null);
        setStatus('anonymous');
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [queryClient]);

  const adoptSession = useCallback((response: LoginResponse) => {
    if (!response.user || !response.tokens) return;
    session.setAccessToken(response.tokens.accessToken);
    setUser(response.user);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (input: LoginInput) => {
      const result = await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: input,
        auth: false,
      });
      if (result.user) adoptSession(result);
      return result;
    },
    [adoptSession],
  );

  const verifyMfa = useCallback(
    async (input: { mfaToken: string; code?: string; recoveryCode?: string }) => {
      const result = await api<LoginResponse>('/auth/mfa/verify', {
        method: 'POST',
        body: input,
        auth: false,
      });
      adoptSession(result);
      return result.user as CurrentUser;
    },
    [adoptSession],
  );

  const refreshUser = useCallback(async () => {
    try {
      const me = await api<CurrentUser>('/me');
      setUser(me);
      setStatus('authenticated');
      return me;
    } catch {
      return null;
    }
  }, []);

  const replaceAccessToken = useCallback(
    async (accessToken: string) => {
      session.setAccessToken(accessToken);
      await refreshUser();
      await queryClient.invalidateQueries();
    },
    [refreshUser, queryClient],
  );

  const clearSession = useCallback(() => {
    session.setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', retryOn401: false });
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const logoutAll = useCallback(async () => {
    try {
      await api('/auth/logout-all', { method: 'POST', retryOn401: false });
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const switchCondominium = useCallback(
    async (condominiumId: string) => {
      const result = await api<Required<Pick<LoginResponse, 'user' | 'tokens'>>>(
        '/auth/switch-condominium',
        {
          method: 'POST',
          body: { condominiumId },
        },
      );
      session.setAccessToken(result.tokens.accessToken);
      setUser(result.user);
      await queryClient.invalidateQueries();
      return result.user;
    },
    [queryClient],
  );

  const value = useMemo(
    () => ({
      status,
      user,
      login,
      verifyMfa,
      adoptSession,
      replaceAccessToken,
      refreshUser,
      logout,
      logoutAll,
      switchCondominium,
    }),
    [
      status,
      user,
      login,
      verifyMfa,
      adoptSession,
      replaceAccessToken,
      refreshUser,
      logout,
      logoutAll,
      switchCondominium,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
